import { tracking } from '@iwoca/frontend-tracking-library';
import _ from 'lodash';

import {
    STEPS,
    ALL_STEPS,
    STEP_ZERO_STEP_ID,
    BUSINESS_INTRODUCTION_STEP_ID,
    PERSONAL_INFORMATION_STEP_ID,
    CHECK_ELIGIBILITY_STEP_ID,
    BUSINESS_ACCOUNTS_STEP_ID,
    MY_ACCOUNTS_STEP_ID
} from '@src/containers/Routes/helper/stepActiveIds.helper';
import { getAmplitudeDefaultEventProperties } from '@src/tracking/amplitudeEvents';
import IwTracking from '@src/tracking/IwTracking';

import { getValueOfLatestActiveSignupStepCookie } from '../tracking/IwTrackingHelpers';

/**
 * Array to dictate order of the signup routing.
 * New signup steps duplicated from IwTrackingHelpers.js.
 * Moving the signup steps to where the ui state is changed and eventually update IwTrackingHelpers to just be getters / setters and not require any logic to add tracking.
 * Using route.js id to match up the string. Decision on where to store these id's will need to be discussed.
 */
export const DEFAULT_SIGNUP_STEPS = [
    STEP_ZERO_STEP_ID,
    BUSINESS_INTRODUCTION_STEP_ID,
    CHECK_ELIGIBILITY_STEP_ID,
    PERSONAL_INFORMATION_STEP_ID,
    BUSINESS_ACCOUNTS_STEP_ID, // Trading history page
    MY_ACCOUNTS_STEP_ID
];

const { bundleHashName = '', bundleDate = '' } = import.meta.env;

export const initialUIState = {
    steps: STEPS,
    activeStep: {
        id: STEPS[0].id
    },
    furthestStep: {
        id: STEPS[0].id,
        name: STEPS[0].name
    },
    manualAddresses: {},
    answeredIsShareholder: {},
    countryModalOpen: false,
    sidebarDropDownOpen: false,
    hasSeenDocumentRequirements: false,
    isNonBusinessIncomeDisabled: null,
    isMonthlyOtherHouseholdIncomeDisabled: null,
    attributes: {},
    version: {
        bundleHashName,
        bundleDate
    }
};

function updateStepData(state, stepToUpdate) {
    return {
        ...state,
        steps: state.steps.map((step) => {
            if (step.id === stepToUpdate.id) {
                return {
                    ...step,
                    ...stepToUpdate
                };
            }

            return step;
        })
    };
}

function updateAttributes(
    state,
    {
        exit_poll,
        uses_eligibility_criteria_journey,
        indicative_offer_flow,
        indicative_offer_flow_direct,
        indicative_offer_enabled,
        show_io_without_ll
    }
) {
    return {
        ...state,
        attributes: {
            exit_poll: !!exit_poll,
            uses_eligibility_criteria_journey:
                uses_eligibility_criteria_journey || false,
            indicative_offer_flow: indicative_offer_flow || false,
            indicative_offer_flow_direct: indicative_offer_flow_direct || false,
            indicative_offer_enabled: indicative_offer_enabled || false,
            show_io_without_ll: show_io_without_ll || false
        }
    };
}

function selectStep(state, selectedStep) {
    const steps = state.steps.map((step) => {
        const active = step.id === selectedStep.id;
        return { ...step, active };
    });

    const furthestStepId = getFurthestStepId(selectedStep.id);

    if (state.furthestStep.id === furthestStepId) {
        return {
            ...state,
            steps,
            activeStep:
                _.find(ALL_STEPS, (step) => step.id === selectedStep.id) ||
                state.activeStep
        };
    }

    const furthestStepPayload = setFurthestStepPayload(furthestStepId);

    return {
        ...state,
        steps,
        activeStep:
            _.find(ALL_STEPS, (step) => step.id === selectedStep.id) ||
            state.activeStep,
        furthestStep: furthestStepPayload
    };
}

function updatePostStatus(state, { id, status }) {
    return {
        ...state,
        steps: state.steps.map((step) => {
            if (step.id === id) {
                return { ...step, buttonStatus: status };
            }

            return step;
        })
    };
}

/**
 * Get furthest step page id from the cookie value.
 *
 * @param {string} nextStepId
 * @returns {string} Signup page name.
 */
const getFurthestStepId = (nextStepId) => {
    const furthestStepCookieValue = getValueOfLatestActiveSignupStepCookie();

    const nextStepIndex = DEFAULT_SIGNUP_STEPS.findIndex(
        (step) => step === nextStepId
    );

    const furthestStepIndex = DEFAULT_SIGNUP_STEPS.findIndex(
        (step) => step === furthestStepCookieValue
    );

    if (nextStepIndex <= furthestStepIndex) {
        return DEFAULT_SIGNUP_STEPS[furthestStepIndex];
    }

    const maxSignupIndex = DEFAULT_SIGNUP_STEPS.length - 1;

    if (nextStepIndex >= maxSignupIndex) {
        return DEFAULT_SIGNUP_STEPS.MY_ACCOUNTS_STEP_ID;
    }

    return DEFAULT_SIGNUP_STEPS[nextStepIndex];
};

/**
 * Set the furthest step page id and name.
 *
 * @param {string} id
 * @returns {object} Signup page id and name.
 */
const setFurthestStepPayload = (id) => {
    const step = ALL_STEPS.filter((step) => {
        return step.id === id;
    })[0];

    tracking.ampli.furthestStep({
        stepId: step.id,
        stepName: step.name,
        ...getAmplitudeDefaultEventProperties()
    });

    return {
        id: step.id,
        name: step.name
    };
};

export default function (state = initialUIState, action) {
    switch (action.type) {
        case 'NEXT_STEP':
            const furthestStepId = getFurthestStepId(action.nextStepId);
            const activeStepInList = _.find(
                state.steps,
                (step) => step.id === action.nextStepId
            );

            const activeStep = activeStepInList || {
                id: action.nextStepId
            };

            IwTracking.setLatestActiveSignupStep(action.nextStepId);

            if (state.furthestStep.id === furthestStepId) {
                return {
                    ...state,
                    activeStep
                };
            }

            const furthestStepPayload = setFurthestStepPayload(furthestStepId);

            return {
                ...state,
                activeStep,
                furthestStep: furthestStepPayload
            };
        case 'SELECT_STEP':
            return selectStep(state, action.selectedStep);
        case 'TOGGLE_MANUAL_ADDRESS':
            return {
                ...state,
                manualAddresses: _.set(
                    { ...state.manualAddresses }, // this is because _.set mutates the object passed in
                    action.path,
                    !_.get(state.manualAddresses, action.path)
                )
            };
        case 'REMOVE_PERSON_MANUAL_ADDRESS_STATE':
            const {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                [action.personUID]: personAddressToDelete,
                ...otherAddresses
            } = state.manualAddresses;

            return {
                ...state,
                manualAddresses: otherAddresses
            };
        case 'TOGGLE_MANUAL_COMPANY_NAME':
            return {
                ...state,
                manualCompanyName: !state.manualCompanyName
            };
        case 'ANSWERED_IS_SHAREHOLDER':
            return {
                ...state,
                answeredIsShareholder: {
                    ...state.answeredIsShareholder,
                    [action.personUID]: true
                }
            };
        case 'UPDATE_STEP_DATA':
            return updateStepData(state, action.step);
        case 'UPDATE_ATTRIBUTES':
            return updateAttributes(state, action.attributes);
        case 'UI_STATE_FETCH_SUCCEED':
            // Excluding attributes section to be sure that we don't overwrite
            // the data coming from the customer attribute endpoint in the fetchInitialApplicationState saga

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { attributes, ...other } = action.ui || {};
            let { steps, furthestStep } = { ...state };

            // Add furthest step amplitude tracking for step 1 on initial load
            if (furthestStep.id === BUSINESS_INTRODUCTION_STEP_ID) {
                tracking.ampli.furthestStep({
                    stepId: furthestStep.id,
                    stepName: furthestStep.name,
                    ...getAmplitudeDefaultEventProperties()
                });
            }

            if (_.get(other, 'activeStep.id') === 'businessProviders') {
                const personalInformationStep = _.find(steps, {
                    id: 'personalInformation'
                });
                other.activeStep = { ...personalInformationStep };
            }

            steps = steps.filter((step) => step.id !== 'businessProviders');

            return {
                ..._.merge({}, state, other, {
                    version: {
                        bundleDate,
                        bundleHashName
                    }
                }),
                steps
            };
        case 'POST_STATUS_UPDATE':
            return updatePostStatus(state, action.data);
        case 'TOGGLE_NON_BUSINESS_INCOME':
            return {
                ...state,
                isNonBusinessIncomeDisabled: action.disabled
            };
        case 'TOGGLE_NON_MONTHLY_OTHER_HOUSEHOLD_INCOME':
            return {
                ...state,
                isMonthlyOtherHouseholdIncomeDisabled: action.disabled
            };
        case 'TOGGLE_COUNTRY_MODAL':
            return {
                ...state,
                countryModalOpen: !state.countryModalOpen
            };
        default:
            return state;
    }
}
